/**
 * React 会话 ↔ 旧版页面 (/static/*, auth-guard.js) 登录态的同步。
 *
 * 旧版把登录态存在 localStorage 的 auth_token / refresh_token / auth_user,
 * 普通用户 (role=user) 登录后默认进旧版 /static/, 所以两边共用同一份会话:
 *
 *   - React 登录 / 注册 / 短信登录 / 选公司成功 → writeLegacySession 写入旧版 key
 *     (格式同旧版 login.html: auth_user = 登录响应里的 user 对象 JSON)。
 *   - React 用 refresh token 换到新 access token → syncLegacyAccessToken:
 *     旧版 key 还是同一个会话时一并更新, 旧页面不会拿着过期 token 被踢。
 *   - React 退出 → removeLegacySession 删掉旧版 key, 旧页面也随之退出。
 *     React 被动清会话 (401) 时只删「同一个会话」的旧版 key; 旧版若已是另一次
 *     登录 (在 login.html 重新登录过) 则保留, 刷新后 React 会接力它。
 *   - React 没有会话而旧版已登录 (旧版 login.html 登录) → readLegacySession 接力。
 *
 * LEGACY_DISMISSED_KEY 是早期版本 (React 不写旧版 key) 留下的 React 自有标记:
 * 「React 已清掉会话时的旧版 token」。现在不再写入, 但仍然尊重它 (同 token 不接力),
 * 并在下一次写入 / 删除旧版会话时顺手清掉。
 */
import type { AuthUser } from "./auth-store";

export const LEGACY_TOKEN_KEY = "auth_token";
export const LEGACY_REFRESH_KEY = "refresh_token";
export const LEGACY_USER_KEY = "auth_user";
/** React 自有 key (不是旧版的); 只读 + 清理, 不再写入 */
export const LEGACY_DISMISSED_KEY = "auth_legacy_dismissed_v1";

type ReadableStorage = Pick<Storage, "getItem">;
type WritableStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

export interface LegacySession {
  token: string;
  refreshToken: string | null;
  user: AuthUser;
}

const ROLES = new Set(["user", "company_admin", "super_admin"]);

function safeGet(storage: ReadableStorage, key: string): string | null {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(storage: WritableStorage, key: string, value: string): void {
  try {
    storage.setItem(key, value);
  } catch {
    /* 隐私模式 / 配额满: 忽略, React 自己的会话不受影响 */
  }
}

function safeRemove(storage: WritableStorage, key: string): void {
  try {
    storage.removeItem(key);
  } catch {
    /* 忽略 */
  }
}

/** 读旧版登录态; 缺 token / user 解析失败 / 旧标记里已被 React 清过时返回 null。只读。 */
export function readLegacySession(storage: ReadableStorage): LegacySession | null {
  const token = safeGet(storage, LEGACY_TOKEN_KEY);
  if (!token) return null;
  if (safeGet(storage, LEGACY_DISMISSED_KEY) === token) return null;
  let user: unknown;
  try {
    user = JSON.parse(safeGet(storage, LEGACY_USER_KEY) || "null");
  } catch {
    return null;
  }
  if (!user || typeof user !== "object") return null;
  const u = user as Partial<AuthUser>;
  if (typeof u.id !== "number" || typeof u.username !== "string" || !ROLES.has(String(u.role))) {
    return null;
  }
  const refreshToken = safeGet(storage, LEGACY_REFRESH_KEY) || null;
  return {
    token,
    refreshToken,
    user: { ...(u as AuthUser), company_id: u.company_id ?? null },
  };
}

/** 旧版 key 里存的是否就是这个 access token (同一个会话) */
export function isLegacyToken(storage: ReadableStorage, token: string | null): boolean {
  return !!token && safeGet(storage, LEGACY_TOKEN_KEY) === token;
}

/**
 * React 登录成功后写入旧版 key, 让 /static/ 页面直接处于登录状态。
 * 格式与旧版 login.html 完全一致: auth_token / refresh_token (可为空串) / auth_user (user JSON)。
 */
export function writeLegacySession(
  storage: WritableStorage,
  token: string,
  refreshToken: string | null,
  user: unknown,
): void {
  safeSet(storage, LEGACY_TOKEN_KEY, token);
  safeSet(storage, LEGACY_REFRESH_KEY, refreshToken || "");
  safeSet(storage, LEGACY_USER_KEY, JSON.stringify(user));
  safeRemove(storage, LEGACY_DISMISSED_KEY);
}

/** refresh 换到新 access token: 旧版 key 仍是同一会话 (= 旧 token) 时同步更新, 否则不动 */
export function syncLegacyAccessToken(
  storage: WritableStorage,
  oldToken: string | null,
  newToken: string,
): void {
  if (isLegacyToken(storage, oldToken)) safeSet(storage, LEGACY_TOKEN_KEY, newToken);
}

/** 删除旧版会话 (同旧版 AuthGuard.clearSession) + 早期版本留下的 React 标记 */
export function removeLegacySession(storage: WritableStorage): void {
  safeRemove(storage, LEGACY_TOKEN_KEY);
  safeRemove(storage, LEGACY_REFRESH_KEY);
  safeRemove(storage, LEGACY_USER_KEY);
  safeRemove(storage, LEGACY_DISMISSED_KEY);
}

export function browserStorage(): Storage | null {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
}
